import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const fmt = (n) => Number(n || 0).toFixed(2);
function authH() {
  const t = window.__cafeApiToken || localStorage.getItem('cafepos_token');
  return { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' };
}

export default function Contractors() {
  const { id } = useParams();
  const navigate = useNavigate();

  if (id) return <ContractorDetail id={id} onBack={() => navigate('/contractors')} />;
  return <ContractorList onSelect={id => navigate(`/contractors/${id}`)} />;
}

// ── Lista kontrahentów ─────────────────────────────────────────────────────
function ContractorList({ onSelect }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [toast, setToast] = useState('');
  const [typeFilter, setTypeFilter] = useState('loyalty'); // company | loyalty

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const load = useCallback(async () => {
    const r = await fetch('/api/contractors', { headers: authH() });
    setList(await r.json());
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleAdd(data) {
    await fetch('/api/contractors', { method: 'POST', headers: authH(), body: JSON.stringify(data) });
    showToast('✓ Dodano');
    setModal(false);
    load();
  }

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Ładowanie...</div>;

  const filteredList = list.filter(c => typeFilter === 'all' || (c.type || 'loyalty') === typeFilter);
  const TYPE_BADGE = {
    company: { label: 'Firma', bg: 'var(--blue-light)', color: 'var(--blue-dark)', icon: '🏢' },
    loyalty: { label: 'Lojalnościowe', bg: '#F0EBF8', color: '#6B3FA0', icon: '👤' },
  };

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '20px 16px' }}>
      {toast && <Toast msg={toast} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600 }}>🏢 Kontrahenci</h2>
        <button onClick={() => setModal(true)} style={{ padding: '9px 16px', background: 'var(--blue)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 500 }}>
          + Nowy
        </button>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {[['company', '🏢 Kontrahenci'], ['loyalty', '👤 Konta lojalnościowe']].map(([key, label]) => (
          <button key={key} onClick={() => setTypeFilter(key)}
            style={{
              padding: '7px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
              border: typeFilter === key ? '0.5px solid var(--blue)' : '0.5px solid var(--border)',
              background: typeFilter === key ? 'var(--blue-light)' : 'none',
              color: typeFilter === key ? 'var(--blue-dark)' : 'var(--text-muted)',
              fontWeight: typeFilter === key ? 500 : 400,
            }}>
            {label}
          </button>
        ))}
      </div>

      {filteredList.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>Brak kontrahentów</div>
      ) : (
        <div style={{ background: 'var(--surface)', borderRadius: 12, border: '0.5px solid var(--border)', overflow: 'hidden' }}>
          {filteredList.map((c, i) => {
            const badge = TYPE_BADGE[c.type || 'loyalty'];
            const prevSortOrder = i > 0 ? filteredList[i - 1].sort_order : null;
            const showGroupGap = i > 0 && c.sort_order !== prevSortOrder;
            return (
            <React.Fragment key={c.id}>
              {showGroupGap && <div style={{ height: 6, background: 'var(--bg)' }} />}
              <div onClick={() => onSelect(c.id)} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', cursor: 'pointer',
                borderBottom: i < filteredList.length - 1 ? '0.5px solid var(--border)' : 'none',
                background: 'var(--surface)', opacity: c.active ? 1 : 0.5,
              }}>
              <span style={{ fontSize: 11, background: badge.bg, color: badge.color, padding: '3px 8px', borderRadius: 4, fontWeight: 500, whiteSpace: 'nowrap' }}>
                {badge.icon} {badge.label}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  {[c.nip && `NIP: ${c.nip}`, c.phone, c.email, !c.active && 'wyłączony'].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: Number(c.balance) > 0 ? '#dc2626' : 'var(--text-muted)' }}>
                  {Number(c.balance) > 0 ? `${fmt(c.balance)} zł` : '—'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {Number(c.balance) > 0 ? 'do spłaty' : 'rozliczony'}
                </div>
              </div>
              <span style={{ marginLeft: 12, color: 'var(--text-muted)' }}>›</span>
              </div>
            </React.Fragment>
            );
          })}
        </div>
      )}

      {modal && <ContractorForm onSave={handleAdd} onClose={() => setModal(false)} />}
    </div>
  );
}

// ── Szczegóły kontrahenta ──────────────────────────────────────────────────
function ContractorDetail({ id, onBack, list = [] }) {
  const [data, setData] = useState(null);
  const [allContractors, setAllContractors] = useState([]);
  const [tab, setTab] = useState('orders'); // orders | discounts | payments
  const [payModal, setPayModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [discountModal, setDiscountModal] = useState(false);
  const [copyModal, setCopyModal] = useState(false);
  const [toast, setToast] = useState('');
  const [expandedOrders, setExpandedOrders] = useState(new Set());

  function toggleOrder(orderId) {
    setExpandedOrders(prev => {
      const next = new Set(prev);
      next.has(orderId) ? next.delete(orderId) : next.add(orderId);
      return next;
    });
  }

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const load = useCallback(async () => {
    const r = await fetch(`/api/contractors/${id}`, { headers: authH() });
    setData(await r.json());
  }, [id]);

  useEffect(() => {
    load();
    fetch('/api/contractors', { headers: authH() }).then(r => r.json()).then(setAllContractors).catch(() => {});
  }, [load]);

  async function handlePay(order_ids, payment_method) {
    const r = await fetch(`/api/contractors/${id}/pay`, {
      method: 'POST', headers: authH(),
      body: JSON.stringify({ order_ids, payment_method }),
    });
    const result = await r.json();
    if (result.ok) {
      showToast(`✓ Spłacono ${result.paid_orders} zamówień — ${fmt(result.total)} zł`);
      setPayModal(false);
      load();
    }
  }

  async function handleEdit(form) {
    await fetch(`/api/contractors/${id}`, { method: 'PATCH', headers: authH(), body: JSON.stringify(form) });
    showToast('✓ Zapisano');
    setEditModal(false);
    load();
  }

  async function handleToggleActive() {
    await fetch(`/api/contractors/${id}`, {
      method: 'PATCH', headers: authH(),
      body: JSON.stringify({ ...data, active: data.active ? 0 : 1 }),
    });
    showToast(data.active ? '✓ Wyłączono konto' : '✓ Włączono konto');
    load();
  }

  async function handleDiscounts(discounts) {
    await fetch(`/api/contractors/${id}/discounts`, { method: 'POST', headers: authH(), body: JSON.stringify({ discounts }) });
    showToast('✓ Zapisano zniżki');
    setDiscountModal(false);
    load();
  }

  async function handleCopyDiscounts(sourceId, sourceName) {
    const r = await fetch(`/api/contractors/${sourceId}/discounts`, { headers: authH() });
    const sourceDiscounts = await r.json();
    const discounts = sourceDiscounts.map(d => ({
      menu_item_id:      d.menu_item_id,
      variant_option_id: d.variant_option_id,
      discount_percent:  d.discount_percent,
      fixed_price:       d.fixed_price,
    }));
    await fetch(`/api/contractors/${id}/discounts`, { method: 'POST', headers: authH(), body: JSON.stringify({ discounts }) });
    showToast(`✓ Skopiowano zniżki od „${sourceName}" (${discounts.length})`);
    setCopyModal(false);
    load();
  }

  if (!data) return <div style={{ padding: 32, textAlign: 'center' }}>Ładowanie...</div>;

  const unpaidOrders = (data.orders || []).filter(o => o.payment_status === 'unpaid');

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '20px 16px' }}>
      {toast && <Toast msg={toast} />}

      {/* Nagłówek */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>←</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h2 style={{ fontSize: 20, fontWeight: 700 }}>{data.name}</h2>
            <span style={{
              fontSize: 11, padding: '3px 8px', borderRadius: 4, fontWeight: 500,
              background: data.type === 'company' ? 'var(--blue-light)' : '#F0EBF8',
              color: data.type === 'company' ? 'var(--blue-dark)' : '#6B3FA0',
            }}>
              {data.type === 'company' ? '🏢 Firma' : '👤 Lojalnościowe'}
            </span>
            {!data.active && (
              <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, fontWeight: 500, background: '#FCEBEB', color: 'var(--red, #A32D2D)' }}>
                wyłączony
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {[data.nip && `NIP: ${data.nip}`, data.phone, data.email].filter(Boolean).join(' · ')}
          </div>
        </div>
        <button onClick={handleToggleActive} style={{ padding: '7px 14px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 13, color: data.active ? 'var(--red, #A32D2D)' : '#16a34a' }}>
          {data.active ? 'Wyłącz' : 'Włącz'}
        </button>
        <button onClick={() => setEditModal(true)} style={{ padding: '7px 14px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 13 }}>
          Edytuj
        </button>
      </div>

      {/* Saldo */}
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 20, marginBottom: 16, border: '0.5px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Saldo do spłaty</div>
          <div style={{ fontSize: 32, fontWeight: 700, color: data.balance > 0 ? '#dc2626' : '#16a34a' }}>
            {fmt(data.balance)} zł
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {unpaidOrders.length} nieopłaconych zamówień
          </div>
        </div>
        {data.balance > 0 && (
          <button onClick={() => setPayModal(true)} style={{ padding: '12px 20px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 15 }}>
            Spłać
          </button>
        )}
      </div>

      {/* Zakładki */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[['orders', '📋 Zamówienia'], ['discounts', '🏷️ Zniżki'], ['payments', '💳 Płatności']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500,
            background: tab === key ? 'var(--blue)' : 'var(--surface)',
            color: tab === key ? '#fff' : 'var(--text-muted)',
          }}>{label}</button>
        ))}
      </div>

      {/* Zamówienia */}
      {tab === 'orders' && (
        <div style={{ background: 'var(--surface)', borderRadius: 12, border: '0.5px solid var(--border)', overflow: 'hidden' }}>
          {(data.orders || []).length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Brak zamówień</div>
          ) : (data.orders || []).map((o, i) => {
            const isOpen = expandedOrders.has(o.id);
            return (
              <div key={o.id} style={{ borderBottom: i < data.orders.length-1 ? '0.5px solid var(--border)' : 'none', overflow: 'hidden' }}>
                <div onClick={() => toggleOrder(o.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>Stolik {o.table_number} #{o.id}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {new Date(o.created_at).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' })}
                    </div>
                  </div>
                  <span style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 500,
                    background: o.payment_status === 'paid' ? '#f0fdf4' : '#fef2f2',
                    color: o.payment_status === 'paid' ? '#16a34a' : '#dc2626',
                  }}>
                    {o.payment_status === 'paid' ? 'Opłacone' : 'Nieopłacone'}
                  </span>
                  <span style={{ fontWeight: 700, fontSize: 15, minWidth: 70, textAlign: 'right' }}>{fmt(o.total)} zł</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{isOpen ? '▲' : '▼'}</span>
                </div>
                {isOpen && (
                  <div style={{ borderTop: '0.5px solid var(--border)', padding: '10px 16px', background: 'var(--bg)' }}>
                    {(o.items || []).map((item, j) => (
                      <div key={j} style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: j < o.items.length-1 ? '0.5px solid var(--border)' : 'none' }}>
                        <span style={{ minWidth: 24, fontWeight: 600, color: 'var(--text-muted)' }}>{item.qty}×</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13 }}>{item.name}</div>
                          {item.variant_label && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{item.variant_label}</div>}
                          {item.modifier_names && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>+ {item.modifier_names}</div>}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>{(Number(item.price) * Number(item.qty)).toFixed(2)} zł</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, fontWeight: 600, fontSize: 14 }}>
                      <span>Razem</span>
                      <span>{fmt(o.total)} zł</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Zniżki */}
      {tab === 'discounts' && (
        <div>
          {data.inherits_from ? (
            <div style={{ background: '#eff6ff', border: '0.5px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#185FA5' }}>
              🔗 Ten kontrahent dziedziczy zniżki od <strong>{allContractors.find(c => c.id === data.inherits_from)?.name || `#${data.inherits_from}`}</strong>.
              Edytuj zniżki u źródłowego kontrahenta — zmiany automatycznie dotyczą tego konta.
            </div>
          ) : (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
            <button onClick={() => setCopyModal(true)} style={{ padding: '8px 16px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)' }}>
              📋 Kopiuj z...
            </button>
            <button onClick={() => setDiscountModal(true)} style={{ padding: '8px 16px', background: 'var(--blue)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
              Edytuj zniżki
            </button>
          </div>
          )}
          <div style={{ background: 'var(--surface)', borderRadius: 12, border: '0.5px solid var(--border)', overflow: 'hidden' }}>
            {(data.discounts || []).length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Brak zniżek</div>
            ) : (data.discounts || []).slice().sort((a, b) => {
              const an = `${a.item_name} ${a.variant_name || ''}`.trim();
              const bn = `${b.item_name} ${b.variant_name || ''}`.trim();
              return an.localeCompare(bn, 'pl');
            }).map((d, i) => (
              <div key={d.id} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: i < data.discounts.length-1 ? '0.5px solid var(--border)' : 'none' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500 }}>{d.item_name}{d.variant_name ? ` — ${d.variant_name}` : ''}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Cena bazowa: {fmt(d.variant_option_id ? d.variant_price : d.item_price)} zł</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {d.fixed_price ? (
                    <div style={{ fontWeight: 700, color: '#16a34a' }}>{fmt(d.fixed_price)} zł</div>
                  ) : (
                    <div style={{ fontWeight: 700, color: '#16a34a' }}>-{d.discount_percent}%</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Płatności */}
      {tab === 'payments' && (
        <div style={{ background: 'var(--surface)', borderRadius: 12, border: '0.5px solid var(--border)', overflow: 'hidden' }}>
          {(data.payments || []).length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Brak płatności</div>
          ) : (data.payments || []).map((p, i) => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: i < data.payments.length-1 ? '0.5px solid var(--border)' : 'none' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{p.payment_method}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {new Date(p.created_at).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' })}
                  {p.notes && ` · ${p.notes}`}
                </div>
              </div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#16a34a' }}>+{fmt(p.amount)} zł</div>
            </div>
          ))}
        </div>
      )}

      {payModal && <PayModal orders={unpaidOrders} onPay={handlePay} onClose={() => setPayModal(false)} />}
      {editModal && <ContractorForm initial={data} onSave={handleEdit} onClose={() => setEditModal(false)} contractors={allContractors} />}
      {!data.inherits_from && discountModal && <DiscountModal contractorId={id} current={data.discounts || []} onSave={handleDiscounts} onClose={() => setDiscountModal(false)} />}
      {copyModal && <CopyDiscountsModal currentId={id} onCopy={handleCopyDiscounts} onClose={() => setCopyModal(false)} />}
    </div>
  );
}

// ── Modals ─────────────────────────────────────────────────────────────────
function ContractorForm({ initial = {}, onSave, onClose, contractors = [] }) {
  const [form, setForm] = useState({
    type: initial.type || 'loyalty',
    name: initial.name || '', email: initial.email || '', phone: initial.phone || '',
    nip: initial.nip || '', notes: initial.notes || '', inherits_from: initial.inherits_from || '',
    sort_order: initial.sort_order ?? 0,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isCompany = form.type === 'company';
  return (
    <Modal title={initial.id ? 'Edytuj kontrahenta' : 'Nowy podmiot'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Typ</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => set('type', 'company')}
              style={{
                flex: 1, padding: 10, borderRadius: 8, cursor: 'pointer', fontSize: 13,
                border: isCompany ? '2px solid var(--blue)' : '0.5px solid var(--border)',
                background: isCompany ? 'var(--blue-light)' : 'none',
                color: isCompany ? 'var(--blue-dark)' : 'var(--text)',
                fontWeight: isCompany ? 500 : 400,
              }}>
              🏢 Firma
            </button>
            <button type="button" onClick={() => set('type', 'loyalty')}
              style={{
                flex: 1, padding: 10, borderRadius: 8, cursor: 'pointer', fontSize: 13,
                border: !isCompany ? '2px solid #6B3FA0' : '0.5px solid var(--border)',
                background: !isCompany ? '#F0EBF8' : 'none',
                color: !isCompany ? '#6B3FA0' : 'var(--text)',
                fontWeight: !isCompany ? 500 : 400,
              }}>
              👤 Lojalnościowe
            </button>
          </div>
        </div>
        {[['name','Nazwa *'], ...(isCompany ? [['nip','NIP']] : []), ['phone','Telefon'], ['email','Email']].map(([k, label]) => (
          <div key={k}>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{label}</label>
            <input value={form[k]} onChange={e => set(k, e.target.value)}
              style={{ width: '100%', padding: '9px 12px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' }} />
          </div>
        ))}
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
            Kolejność <span style={{ fontWeight: 400 }}>(mniejsza liczba = wyżej na liście)</span>
          </label>
          <input type="number" value={form.sort_order} onChange={e => set('sort_order', parseInt(e.target.value) || 0)}
            style={{ width: '100%', padding: '9px 12px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Notatki</label>
          <div className="field">
            <label>Dziedzicz zniżki od</label>
            <select value={form.inherits_from || ''} onChange={e => set('inherits_from', e.target.value || null)}
              style={{ width: '100%', padding: '9px 12px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--bg)', color: 'var(--text)', fontSize: 14 }}>
              <option value="">— własne zniżki —</option>
              {contractors.filter(c => c.id !== initial.id).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3}
            style={{ width: '100%', padding: '9px 12px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box', resize: 'vertical' }} />
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 12, border: '0.5px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>Anuluj</button>
          <button onClick={() => form.name && onSave(form)} disabled={!form.name}
            style={{ flex: 2, padding: 12, background: form.name ? 'var(--blue)' : 'var(--border)', color: form.name ? '#fff' : 'var(--text-muted)', border: 'none', borderRadius: 8, cursor: form.name ? 'pointer' : 'not-allowed', fontWeight: 600 }}>
            Zapisz
          </button>
        </div>
      </div>
    </Modal>
  );
}

function PayModal({ orders, onPay, onClose }) {
  const [selected, setSelected] = useState(null); // null = wszystkie
  const [method, setMethod] = useState('cash');

  const total = selected
    ? orders.filter(o => selected.includes(o.id)).reduce((s, o) => s + parseFloat(o.total || 0), 0)
    : orders.reduce((s, o) => s + parseFloat(o.total || 0), 0);

  function toggle(id) {
    if (!selected) { setSelected(orders.map(o => o.id).filter(x => x !== id)); return; }
    if (selected.includes(id)) setSelected(selected.filter(x => x !== id));
    else setSelected([...selected, id]);
  }

  return (
    <Modal title="Spłata zamówień" onClose={onClose}>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer', marginBottom: 10 }}>
          <input type="checkbox" checked={!selected} onChange={() => setSelected(null)} />
          Wszystkie nieopłacone ({orders.length})
        </label>
        <div style={{ maxHeight: 200, overflowY: 'auto', border: '0.5px solid var(--border)', borderRadius: 8 }}>
          {orders.map(o => (
            <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', borderBottom: '0.5px solid var(--border)' }}>
              <input type="checkbox" checked={!selected || selected.includes(o.id)} onChange={() => toggle(o.id)} />
              <span style={{ flex: 1, fontSize: 13 }}>Stolik {o.table_number} #{o.id} · {new Date(o.created_at).toLocaleDateString('pl-PL')}</span>
              <span style={{ fontWeight: 600 }}>{fmt(o.total)} zł</span>
            </label>
          ))}
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Forma płatności</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
          {[['cash','💵 Gotówka'],['card','💳 Karta'],['blik','📱 BLIK'],['transfer','🏦 Przelew'],['receipt','🧾 Paragon']].map(([k,l]) => (
            <button key={k} onClick={() => setMethod(k)} style={{ padding: '8px 4px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, background: method===k ? 'var(--blue)' : 'var(--bg)', color: method===k ? '#fff' : 'var(--text-muted)' }}>{l}</button>
          ))}
        </div>
      </div>
      <div style={{ borderTop: '0.5px solid var(--border)', paddingTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontWeight: 500 }}>Razem</span>
        <span style={{ fontSize: 22, fontWeight: 700 }}>{fmt(total)} zł</span>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onClose} style={{ flex: 1, padding: 12, border: '0.5px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer' }}>Anuluj</button>
        <button onClick={() => onPay(selected, method)} style={{ flex: 2, padding: 12, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
          Spłać {fmt(total)} zł
        </button>
      </div>
    </Modal>
  );
}

function DiscountModal({ contractorId, current, onSave, onClose }) {
  const [menuItems, setMenuItems] = useState([]);
  const [discounts, setDiscounts] = useState(current.map(d => ({ ...d })));
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetch('/api/menu/all', { headers: authH() })
      .then(r => r.json())
      .then(cats => setMenuItems(cats.flatMap(c => (c.items || []).map(i => ({ ...i, category: c.name })))));
  }, []);

  function getDiscount(itemId, variantId = null) {
    return discounts.find(d => d.menu_item_id === itemId &&
      (variantId ? d.variant_option_id == variantId : !d.variant_option_id));
  }

  function setDiscount(itemId, variantId, field, value) {
    setDiscounts(prev => {
      const existing = prev.find(d => d.menu_item_id === itemId &&
        (variantId ? d.variant_option_id == variantId : !d.variant_option_id));
      if (existing) return prev.map(d =>
        d.menu_item_id === itemId && (variantId ? d.variant_option_id == variantId : !d.variant_option_id)
          ? { ...d, [field]: value } : d);
      return [...prev, { menu_item_id: itemId, variant_option_id: variantId || null, discount_percent: 0, fixed_price: null, [field]: value }];
    });
  }

  function removeDiscount(itemId, variantId = null) {
    setDiscounts(prev => prev.filter(d =>
      !(d.menu_item_id === itemId && (variantId ? d.variant_option_id == variantId : !d.variant_option_id))));
  }

  const filtered = menuItems.filter(i => i.name.toLowerCase().includes(search.toLowerCase()));

  function renderDiscountRow(itemId, variantId, basePrice, label, isVariant) {
    const d = getDiscount(itemId, variantId);
    return (
      <div key={`${itemId}-${variantId}`} style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: isVariant ? '8px 12px 8px 28px' : '10px 12px',
        borderBottom: '0.5px solid var(--border)',
        background: isVariant ? 'var(--bg)' : 'var(--surface)',
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: isVariant ? 12 : 13, fontWeight: isVariant ? 400 : 500, color: isVariant ? 'var(--text-muted)' : 'var(--text)' }}>{label}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{Number(basePrice).toFixed(2)} zł</div>
        </div>
        {d ? (
          <>
            <input type="number" placeholder="%" value={d.discount_percent || ''} min="0" max="100"
              onChange={e => setDiscount(itemId, variantId, 'discount_percent', e.target.value)}
              style={{ width: 52, padding: '6px 8px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'var(--bg)', color: 'var(--text)', textAlign: 'center', fontSize: 13 }} />
            <input type="number" placeholder="Cena zł" value={d.fixed_price || ''} min="0"
              onChange={e => setDiscount(itemId, variantId, 'fixed_price', e.target.value || null)}
              style={{ width: 72, padding: '6px 8px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'var(--bg)', color: 'var(--text)', textAlign: 'center', fontSize: 13 }} />
            <button onClick={() => removeDiscount(itemId, variantId)}
              style={{ padding: '4px 8px', border: 'none', background: '#fef2f2', color: '#dc2626', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>✕</button>
          </>
        ) : (
          <button onClick={() => setDiscount(itemId, variantId, 'discount_percent', 0)}
            style={{ padding: '6px 10px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'var(--bg)', cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            + Zniżka
          </button>
        )}
      </div>
    );
  }

  return (
    <Modal title="Zniżki dla kontrahenta" onClose={onClose} wide>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Szukaj produktu..."
        style={{ width: '100%', padding: '8px 12px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--bg)', color: 'var(--text)', marginBottom: 12, boxSizing: 'border-box' }} />
      <div style={{ maxHeight: 400, overflowY: 'auto', border: '0.5px solid var(--border)', borderRadius: 8, marginBottom: 16 }}>
        {filtered.map(item => (
          <div key={item.id}>
            {/* Produkt bez wariantów */}
            {(!item.variant_groups || item.variant_groups.length === 0) &&
              renderDiscountRow(item.id, null, item.price, item.name, false)}
            {/* Produkt z wariantami — tylko nagłówek + wiersze wariantów */}
            {item.variant_groups?.length > 0 && (
              <>
                <div style={{ padding: '8px 12px', background: 'var(--surface)', borderBottom: '0.5px solid var(--border)', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>
                  {item.name}
                </div>
                {item.variant_groups.flatMap(g => g.options.map(opt =>
                  renderDiscountRow(item.id, opt.id, opt.price, `${g.name}: ${opt.name}`, true)
                ))}
              </>
            )}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onClose} style={{ flex: 1, padding: 12, border: '0.5px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer' }}>Anuluj</button>
        <button onClick={() => onSave(discounts.filter(d => d.discount_percent > 0 || d.fixed_price))}
          style={{ flex: 2, padding: 12, background: 'var(--blue)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
          Zapisz zniżki
        </button>
      </div>
    </Modal>
  );
}
function CopyDiscountsModal({ currentId, onCopy, onClose }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [copying, setCopying] = useState(null);

  useEffect(() => {
    fetch('/api/contractors', { headers: authH() })
      .then(r => r.json())
      .then(data => { setList(data.filter(c => String(c.id) !== String(currentId))); setLoading(false); });
  }, [currentId]);

  const filtered = list.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));

  async function handlePick(c) {
    setCopying(c.id);
    await onCopy(c.id, c.name);
    setCopying(null);
  }

  return (
    <Modal title="Kopiuj zniżki od kontrahenta" onClose={onClose}>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Szukaj kontrahenta..."
        style={{ width: '100%', padding: '8px 12px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--bg)', color: 'var(--text)', marginBottom: 12, boxSizing: 'border-box' }} />

      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Ładowanie...</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Brak innych kontrahentów</div>
      ) : (
        <div style={{ maxHeight: 320, overflowY: 'auto', border: '0.5px solid var(--border)', borderRadius: 8, marginBottom: 16 }}>
          {filtered.map((c, i) => (
            <div key={c.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              borderBottom: i < filtered.length - 1 ? '0.5px solid var(--border)' : 'none',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{c.name}</div>
                {c.nip && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>NIP: {c.nip}</div>}
              </div>
              <button onClick={() => handlePick(c)} disabled={copying === c.id}
                style={{ padding: '6px 14px', background: 'var(--blue)', color: '#fff', border: 'none', borderRadius: 6, cursor: copying === c.id ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 500, opacity: copying === c.id ? 0.6 : 1 }}>
                {copying === c.id ? 'Kopiuję...' : 'Kopiuj'}
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
        ⚠️ Skopiowanie nadpisze obecne zniżki tego kontrahenta.
      </div>

      <button onClick={onClose} style={{ width: '100%', padding: 12, border: '0.5px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer' }}>
        Zamknij
      </button>
    </Modal>
  );
}

function Modal({ title, children, onClose, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 16 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: wide ? 560 : 380, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Toast({ msg }) {
  return (
    <div style={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', background: '#1e293b', color: '#fff', padding: '10px 20px', borderRadius: 8, zIndex: 999, fontSize: 14 }}>
      {msg}
    </div>
  );
}
